import type { UiLang } from "./i18n/catalogues";
// Live bubble timing (EM-11). ASSUMPTION: these thresholds are unmeasured assumptions, not product
// numbers; keep them here and nowhere else.
export const LIVE_BUBBLE_BIRTH_MS = 300; // sustained speech before a live bubble is born
export const LIVE_BUBBLE_STILL_WRITING_MS = 3_000; // bubble shows "still writing"
export const LIVE_BUBBLE_FAILURE_MS = 10_000; // bubble enters its failure state
// MAJLIS-73, consultant ruling 2 (2026-09-22): a live bubble the voice gate takes away (segment_dropped
// {other_speaker}) FADES out, never jumps. ASSUMPTION: the ruling says "short" and fixes no length; this is the
// spec's own fade (UI-C18/UI-M5, words land with a 350 ms fade). app.css's `.bubble.fading` carries the same number.
export const LIVE_BUBBLE_FADE_MS = 350;

// Mic ring (UI-C19, EM-12): "we can't hear you" when the local level has been high this long with no
// phrase open for this seat (no speech_start, or one already closed). ASSUMPTION (EM-12): 2 s is from the
// spec; the level that counts as "high" is not fixed by the spec and is an assumption (RMS of the
// analyser's time-domain samples, 0..1).
export const MIC_UNHEARD_MS = 2_000;
// MAJLIS-116 (UI-C19 amended 2026-09-25): this is ONLY the "we can't hear you" trigger (and the idle/hearing
// boundary below it) - it never gates the ring's ACTIVE `heard` state, which follows the server's speech_start
// regardless of level (media/micRing.ts). ~-22 dBFS; his transcribed phrases measured -35..-50 dBFS RMS
// (Toronto dev box, MAJLIS-116), well under this, which is exactly why `heard` cannot depend on it.
export const MIC_HIGH_LEVEL = 0.08; // ASSUMPTION: not in the spec, awaiting the consultant

// EngineStatusBanner (UI-C20): after the room's writing comes back, "Writing again" shows this long, then the slot
// clears. The consultant's value of 2026-09-19 (UI tab row UI-C20); not measured.
export const WRITING_AGAIN_MS = 2_000;

// RoomModeSwitch notice (UI-C17, MAJLIS-63): after the host switches the room's mode, the one-line notice shows this
// long in the one banner slot, after the socket and engine banners. The consultant's value of 2026-09-23; not measured.
export const MODE_NOTICE_MS = 2_000;

// ListenActivity (UI-C29, MAJLIS-88): the first time per room that the Listen control shows activity (Listen off, another
// seat speaking), the hint "Tap to hear them" shows beside it this long, then not again in this room. ASSUMPTION
// (UI-C29, the approved mock-up's label): 4 s; not measured.
export const LISTEN_HINT_MS = 4_000;
// The speaking language (MAJLIS-44, D-34, P-17b). ASSUMPTION (the approved mock-up's, docs/spec/prototype/
// speak-language-mockup.html, consultant 2026-09-23): the one-line notice of a confirmed change - "You now speak
// {lang}" on this phone, "{name} now speaks {lang}" on every other - shows this long in the one banner slot, at its
// LOWEST rank (below the socket, engine and mode lines); not measured.
export const SPEAK_NOTICE_MS = 3_000;
// ASSUMPTION (the same mock-up's): a change the server has neither confirmed (seat_updated) nor refused
// (speak_lang_refused) this long after the tap counts as refused, and the picker shows its refusal line; not measured.
export const SPEAK_REPLY_TIMEOUT_MS = 5_000;
// The server's own limit, one change per 10 s per seat (consultant ruling 1, ASSUMPTION there), held equal to
// constants.SPEAK_LANG_MIN_INTERVAL_S of docs/protocol.schema.json. The app does not enforce it: the server refuses
// out loud, and this is here so the two are pinned together.
export const SPEAK_CHANGE_INTERVAL_S = 10;

// ReplayButton (UI-C5): after a failed replay (network, decode, a refused play()) the button shows its `error` look
// this long, then idle again. The consultant's value of 2026-09-19; not measured.
export const REPLAY_ERROR_MS = 1_500;

// Waveform and level sampling (UI-M5): about 9 Hz.
export const LEVEL_TICK_MS = 110;
export const WAVE_BARS = 24;

// Seat names in the header (UI-C8, D-31): the largest room in which a speaking chip still expands to carry
// the speaker's name. Eight seats is the ceiling and two of them can be speaking at once; at 390 px that
// leaves the row no width for a name beside the dots, and a name shrunk to an illegible sliver is worse than
// no name at all. Past this many seats the chip keeps the dot alone — and nothing is lost by it, because the
// speaker's live bubble in the canvas carries the full name, in the same seat colour, at the same moment
// (D-24). The header's name is a convenience in a small room and clutter in a full one.
// app.css derives its selector from this number (:nth-child(SEAT_NAMES_MAX + 1)) and room.test.tsx holds the
// two in step. The name is taken out of the LAYOUT there, never out of the document.
export const SEAT_NAMES_MAX = 6;

// Leave (critique 2026-09-19, decision 4): the "Rejoin" toast after leaving has no timer since MAJLIS-135 (T-161,
// consultant ruling 3): it stays until dismissed, so no constant is kept for it.

// Protocol heartbeat (docs/protocol.md "Heartbeat"; a test checks these against the schema's constants).
export const HEARTBEAT_MS = 15_000;
export const HEARTBEAT_MISSED = 2; // silence from the server for 2 intervals = a dead connection

// Reconnect backoff after a dropped socket (UI-C12). ASSUMPTION: the spec fixes no schedule.
export const RECONNECT_BACKOFF_MS = [0, 1_000, 2_000, 4_000, 8_000];

// Asking again for a LiveKit grant (MAJLIS-9, consultant ruling 2026-09-19): LiveKit gave the connection up while the
// room socket stayed live (a phone moving between Wi-Fi and 5G). The first media_grant request goes at once; after a
// refusal (media_rate_limited) or a failed connect with the fresh grant, the next waits 10 s, then 20 s, then 40 s,
// capped at 60 s; a successful reconnect resets it. ASSUMPTION: the schedule is the consultant's assumption, not
// measured; the server allows 6 media_grant requests per minute per seat.
export const MEDIA_GRANT_BACKOFF_MS = [10_000, 20_000, 40_000, 60_000];
// A media_grant request with no reply (a grant, null, or media_rate_limited) by then counts as a refusal: the next
// request follows the backoff above, and a reply arriving later is not used. ASSUMPTION (consultant 2026-09-19): 10 s.
export const MEDIA_GRANT_REPLY_TIMEOUT_MS = 10_000;

// The microphone stopped (MAJLIS-71, D-52, UI-C19, EM-12; media/micKeeper.ts). When the microphone's track ENDS while
// the seat is unmuted, one SILENT re-open is tried (no tap) while the page is visible. Both numbers are ASSUMPTIONS, to
// be tuned after the owner's phone test (blocks A and B of the MAJLIS-71 proposal), which has NOT been run.
// A silent re-open still pending this long counts as failed: the tap state shows, and a late result is stopped, never
// used. The proposal's own "pending after 10 s" line of its owner test (step 7); WebKit holds a getUserMedia call until
// the page is visible and focused, so an unbounded wait could hang for ever. ASSUMPTION: 10 s.
export const REOPEN_TIMEOUT_MS = 10_000;
// A re-opened track that ends again within this long of its re-open is not re-opened silently again (no silent loop):
// the tap state shows at once. Twice Chrome's own ~15 s end of a capture that delivers nothing (discuss-webrtc PSA,
// 2017-06-05), so such a device is caught on its first repeat. ASSUMPTION: 30 s.
export const FLAP_WINDOW_MS = 30_000;

// Translation (MAJLIS-49, D-06, D-17). The server backfills this many of the room's latest phrases into a reading
// language chosen mid-room (consultant ruling (a), 2026-09-22; ASSUMPTION: the visible window). A test holds it
// equal to the server's BACKFILL_SEGMENTS through docs/protocol.schema.json.
export const TRANSLATION_WINDOW = 20;
// How long a final phrase's translation slot shows "pending" before it gives up and shows nothing (a failure sends
// no message: the original simply stands). ASSUMPTION, derived from the server's own worst case for one job, not
// measured: the 20 s timeout on the primary, then the 20 s timeout on the outage fallback, plus the one 1 s 429
// backoff and slack. The measured case is +884 ms p50 / 1386 ms p95 (Toronto dev box, 2026-09-22).
export const TRANSLATION_WAIT_MS = 45_000;

// MAJLIS-113 (UI-C34, D-54, PROPOSED): the isolator's compare mode. A FINAL phrase of this seat (one that started at or
// after compare_mode.since) with no comparison partner shows the dashed "(nothing)" placeholder once this long has
// passed since it became final; until then its slot waits ("isolator: appears once this phrase is final"), and a
// partner that arrives later replaces the placeholder. ASSUMPTION, not measured: the ghost seat hears the same frames a
// processor delay later (milliseconds) and is transcribed beside the real seat, so its final normally lands with the
// real one; 5 s leaves room for a rescue verdict on the ghost's side (D-29 routing) - the real engine's rescue time on
// this box has not been measured for a ghost.
export const COMPARE_ABSENT_MS = 5_000;

// ---------------------------------------------------------------- MAJLIS-64: the phone's voice queue (D-43, D-08)
// SAFETY FLAG (the orchestrator's decision): the whole AUTO-PLAY voice queue (interpreter
// mode, D-43) is behind this one switch. It was OFF until the owner reviewed the mock-ups UI-C21..C25 ("GO",
// 2026-09-23) and the UI was built; it is ON from the build that carries that UI (MAJLIS-64 voice UI), because the
// person now has the control on screen: the Listen choice (UI-C21) switches to the original, and Listen off, a
// screen-only room or room_state.voice false stop it too. Off means the queue never asks for a voice by itself and
// the room offers no interpreter choice; tap-to-voice (UI-C6) is not behind it. The tests keep one injected-off row
// as the control: nothing is ever requested.
export const VOICE_QUEUE_ENABLED = true;

// D-43(2), consultant ruling B: while a spoken translation plays, the live voices are lowered to this volume (0..1),
// except on iOS where `volume` is ignored and they are MUTED instead. ASSUMPTION ("about a quarter"): the level is
// measured on the owner's phones.
export const LIVE_UNDER = 0.25;

// D-43(3), UI-C23: catch-up. When the phrase at the head of the queue is older than this at the moment it would start
// (now minus its P-10 arrival on this phone), the backlog collapses to the NEWEST phrase; the one playing finishes.
// ASSUMPTION (D-43: "~8 s", tested on his phones).
export const VOICE_CATCHUP_MS = 8_000;

// MAJLIS-119 (G5, D-43, consultant ruling (a), 2026-09-25): the voice queue plays in phrase START order (t0). When the
// earliest-started phrase not yet voiced has no translation while a later one is ready, the queue waits this long for
// it, then skips it and plays the next ready phrase (the skipped one is voiced when it lands, if inside catch-up).
// ASSUMPTION, derived from one measurement: translation ready after phrase end p95 1.62 s (p50 -0.10 s), the owner's
// replay room, Toronto dev box, 2026-09-25 - not a product latency; to be re-checked on the owner's phones.
export const VOICE_ORDER_WAIT_MS = 2_000;

// D-43(4), UI-C24, G9: after the person taps the mic (takes the floor), the queue stays quiet until THIS seat has had
// no phrase open for this long - counted from the tap, and again from the close of each of his own phrases - then it
// resumes from the newest phrase. ASSUMPTION: not in the spec; the orchestrator's brief says "when they stop
// speaking", and this is how "stopped" is read from the room's own events (speech_start / its terminal event).
export const VOICE_FLOOR_QUIET_MS = 2_000;

// The fuse on a voice that does not start: from the POST /api/voice until the element's play() says the sound began.
// A voice that neither starts nor fails by then is skipped (the text stands) and the next one is tried. ASSUMPTION:
// the provider's first byte is 0.18-0.44 s p95 (Toronto dev box, MAJLIS-64 phase 1); the phone's buffering before
// `playing` is UNMEASURED, so 8 s is generous and is to be checked on the owner's iPhone.
export const VOICE_START_FUSE_MS = 8_000;

// EM-25 for a voice: no spoken phrase outlasts this; past it the gate's hold is force-released with a warning (a gate
// that fails shut is a person talking to nobody). ASSUMPTION: a 20 s phrase voiced in another language can run longer
// than its original, so this is replay's 30 s plus half again.
export const VOICE_MAX_HOLD_MS = 45_000;

// MAJLIS-140 (D-59, P-39): a passive seat's voice track. When a clip is asked for while the phone is NOT subscribed to
// the track (Auto-play OFF, a tap), it subscribes first and waits at most this long for LiveKit to say so before the
// request goes out; past it the clip fails "subscribe" (the switch's line, and a gap record). ASSUMPTION: a
// resubscribe on a live connection is one signalling round trip (hundreds of ms); UNMEASURED on the owner's phones.
export const VOICE_TRACK_SUBSCRIBE_MS = 3_000;

/** MAJLIS-144 (P-39, T-166): a passive seat's `voice_played` carries its request -> playing ms clamped to 0..this -
 * the server's VOICE_PLAYED_MAX_MS (ASSUMPTION there: longer is no clip); a test pins the two together. */
export const VOICE_PLAYED_MAX_MS = 60_000;

/** MAJLIS-65 (P-16, UI-C16): a voice's name, at most this many characters once trimmed - the server's
 * VOICE_NAME_MAX_LEN (ASSUMPTION there, bounded like a seat name); a test pins the two together. */
export const VOICE_NAME_MAX = 40;

// ---------------------------------------------------------------- MAJLIS-109 (UI-C9, D-34): the two-part chip
// The seat-stack's own "sensible minimum" width - roughly three overlapping seat dots (24 px each, -7 px
// overlap so 17 px pitch, MAJLIS-44's own seat-chip rule) plus the stack's 8 px x 2 padding: 24 + 2*17 + 16 =
// 74. ASSUMPTION, not the owner's number: a stack narrower than this shows at most one full dot and a
// sliver, which reads as broken rather than merely crowded. Documentation only below this point - nothing
// reads MIN_SEAT_STACK_PX at runtime; it is the reasoning LANG_CHIP_COMPACT_BELOW_PX was derived from, kept
// beside it so the derivation does not go stale unread.
export const MIN_SEAT_STACK_PX = 74;

/** MAJLIS-109 (UI-C9, D-34, MAJLIS-44 ruling 8): below this viewport width each half of the room header's
 * two-part chip (speak | read) shows its BCP-47 CODE ("AR", "EN", "ID") instead of its full name, so the
 * seat-stack does not squeeze under MIN_SEAT_STACK_PX. The bound is a VIEWPORT width, not a per-language one:
 * `.chip-text`'s 12ch ellipsis already caps how wide any language's name can render (MAJLIS-44's own rule),
 * so "Bahasa Indonesia" - the one name the ellipsis cuts (MAJLIS-44's finding) - sets the worst case for
 * every language the app will ever add, not just the 30 shipped today.
 *
 * MEASURED (Toronto dev box, headless Chromium, web/tools/measure, layout only; MAJLIS-109): with that worst
 * case chip pair (English interface, speaking Indonesian) the seat-stack was 38.9 px at 320 px, 78.9 px at
 * 360 px, 108.9 px at 390 px; the Arabic interface's own worst case (170.9 px pair) left more room at every
 * width (50.8 / 90.8 / 120.8 px). 360 is the narrowest of the three widths this rig checks whose EN-interface
 * seat-stack still clears MIN_SEAT_STACK_PX (78.9 >= 74); 320 does not (38.9 < 74). ASSUMPTION: the bound
 * itself is chosen from those three points, not swept to its exact pixel; real iOS text metrics differ
 * (web/tools/measure/README.md's own caveat).
 *
 * MECHANISM, decided here rather than left to a CSS container query: a container query would need BOTH the
 * name and the code in the DOM at once (one hidden by CSS), and every existing test that reads
 * `speakChip.textContent` - a real DOM property that ignores `display: none` in every browser, not only
 * jsdom - would see both strings concatenated. So this is the "measured-width approach" the ticket allows
 * instead: `App.tsx`'s `chipCompact` prop, read once at mount from `AppDeps.chipCompact` (main.tsx,
 * `window.matchMedia`) exactly as `reducedMotion` already is, renders ONE text node. main.tsx imports this
 * constant into its own media-query string, so there is nothing here for a second literal to drift from. */
export const LANG_CHIP_COMPACT_BELOW_PX = 360;

/** MAJLIS-163 (UI-C10, the consultant's ruling 1 of 2026-10-04): the narrowest viewport at which the bottom bar's one-word
 * labels and the Leave pill were MEASURED to fit - each on one line, none cut, the bar not overflowing - in BOTH
 * interfaces, with the bar live AND muted (the mic's label follows the action: Mute / Unmute, «كتم» / «رفع الكتم»).
 * Nothing reads it at runtime: no width hides or shortens a label (app.css has no media rule on .bar-label); this is
 * where the measurement behind "the labels fit at 320 px" is written down.
 *
 * MEASURED (Toronto dev box, headless Chromium 152, web/tools/measure claim "room-controls", layout only; 2026-10-04,
 * the tree of the MAJLIS-163 Unmute/order commit on d167102, measured as `--ref worktree` before it was committed - only
 * this comment and the spec changed after; a guest in a 3-seat live room - one digit at any size, 8 seats at most).
 * At 320 px the bar is 288 px client / 288 px scroll in every state. English: "Listen" 33.1 px, "People · 3" 54.8 px,
 * "Mute" 27.7 px / "Unmute" 43.7 px, each one line, cut false; the Leave pill "Leave" 89.7 x 44 px, cut false; the
 * start side 108 px holds 104.8 px. Arabic: «استماع» 33.1 px, «الحاضرون · 3» 61.8 px, «كتم» 16.2 px / «رفع الكتم»
 * 42.6 px (inside the 56 px mic item), one line, cut false; the pill «مغادرة» 87.5 x 44 px; the start side takes its
 * min-content 112 px (111.8 px of items) and the end side keeps 104 px for the pill, so at 320 px in Arabic the mic
 * sits 4 px off the bar's centre - ACCEPTED by the consultant (2026-10-04). At 360 and 390 px both interfaces keep
 * equal 128 / 143 px sides, no label cut, live or muted.
 *
 * THE HEADER'S MODE LINE (ordered mode · Locked · Listed · N people, so its ellipsis cuts the count first), shown px of
 * needed px in the same runs: unlocked and unlisted - English "Live audio · 3 people" 91 of 112 at 320, 99 of 112 at
 * 360, whole (112) at 390; Arabic «صوت مباشر · 3 أشخاص» 86 of 111 at 320, whole (111) at 360 and 390. Locked and
 * listed - English "Live audio · Locked · Listed · 3 people" 106 / 115 / 139 of 195 at 320 / 360 / 390; Arabic
 * «صوت مباشر · مقفل · معروضة · 3 أشخاص» 100 / 134 / 157 of 187: cut at every width this rig checks, the count first.
 *
 * THE SEAT PILL in that same locked AND listed room at 320 px narrows to 49.8 x 44 px (English) / 49.4 x 44 px
 * (Arabic) - about two dots, below MIN_SEAT_STACK_PX (74), which guards the dots, not the count - and never below its
 * 44 x 44 px tap target (min-inline-size / min-block-size 44px). ACCEPTED (the consultant, 2026-10-04): the pill holds
 * dots only, as the mock-up does; the count is always visible in the bar's "People · N" (measured above to fit at every
 * width), and the pill's accessible name carries it ("People · N"). Ordinary room at 320: 64.9 x 44 en / 62.9 x 44 ar.
 * ASSUMPTION: real iOS text metrics differ (web/tools/measure/README.md); the Arabic start side has 0.2 px to spare at
 * 320 px.
 *
 * MAJLIS-164 (ruling 10), MEASURED for all 30 interface languages (Toronto dev box, headless Chromium 152, web/tools/
 * measure --langs, layout only; a guest, then the host, in a live 3-seat room). 2026-10-04T10:07Z (b248137;
 * ~/majlis-evidence/MAJLIS-164/measure-all-20261004T100654Z/): at 320 px Greek (300 px in 288: the Leave pill «Αποχώρηση»
 * 134.1 px, muted «Κατάργηση σίγασης» 111.3 px) and French (291 in 288: "Participants · 3" 83.4 px) overflowed. The
 * consultant's Addendum 3 chose shorter words; RE-MEASURED 2026-10-04T10:16Z (~/majlis-evidence/MAJLIS-164/
 * measure-all-20261004T101543Z/): no bar overflows in any language at 320, 360 or 390, live or muted - at 320 Greek 288 /
 * 288 (Leave «Έξοδος» 100.5 px, muted «Άρση σίγασης» 77.7 px), French 288 / 288 ("Présents · 3" 65.4 px). No label is
 * cut and none wraps. */
export const BAR_LABELS_FIT_PX = 320;

/** MAJLIS-175 (the owner-approved screen-rules mock-up of 2026-10-05, frame (o)): on a screen narrower than this the
 * compare-mode pill in the room header keeps only its mark (the dot in a 32 x 32 px ring); from this width up it shows
 * the word. The mock-up's own bound. app.css holds the media query at this width less 0.02 px (comparePill in
 * screenRules.test.tsx). Why: at 320 px in English the word pushed the room's code onto the title's chevron (the
 * contact sheet's one known overlap hit, compare--en-320, MAJLIS-167). */
export const COMPARE_WORD_FROM_PX = 360;

// MAJLIS-67 (P-21, D-45): while the open record still has translations on their way (`pending`), it is read again this
// often. ASSUMPTION: a translation takes about 1.4 s p95 (MAJLIS-49 phase 1, Toronto dev box), so 2.5 s usually finds
// the next window done; not measured on a phone.
export const RECORD_POLL_MS = 2_500;
// ...and it stops asking after this many reads in a row that brought no new translation: what is still pending then is
// shown as the original alone (a failed translation sends nothing, as live). ASSUMPTION.
export const RECORD_POLL_IDLE_MAX = 3;

/** MAJLIS-67 (P-22): a search is at most this many characters - the server's RECORD_SEARCH_MAX_LEN (ASSUMPTION there);
 * a test pins the two together. */
export const RECORD_SEARCH_MAX = 200;
// MAJLIS-67 (P-22): the search asks the server this long after the last keystroke ("it matches as you type", 2b).
// ASSUMPTION: long enough not to send a request per letter, short enough to feel immediate.
export const RECORD_SEARCH_DEBOUNCE_MS = 250;

/** MAJLIS-66 (P-30): a read link's name is at most this many characters - the server's READ_LINK_LABEL_MAX_LEN
 * (ASSUMPTION there, a seat name's bound); a test pins the two together. */
export const READ_LINK_LABEL_MAX = 40;
// MAJLIS-153 (UI-C42, P-40): the summary screen POSTs again after each 202's Retry-After, and gives up this long after
// its first POST with the failure line and "Try again". ASSUMPTION (UI-C42's own 120 s): the first summary was
// estimated at about 10-20 s (D-60); MAJLIS-152 then measured whole answers of 1.9-15.7 s across four models
// (Toronto dev box, never product latency; EM-35's amendment of 2026-10-02). The server keeps writing a job the phone
// gave up on.
export const SUMMARY_GIVE_UP_MS = 120_000;
/** MAJLIS-153 (UI-C40, UI-C41; consultant rulings of 2026-10-01 and 2026-10-02): on a viewport strictly narrower than
 * this the record header's day drops its weekday ("Sep 29") and its meta line is the duration alone ("48 min"); at
 * this width and up, the full day and the full seat line. The header takes the mock-up's geometry (gap 0, 6 px
 * padding, the chip 4 px off its neighbour).
 *
 * MEASURED (Toronto dev box, headless Chromium 152, web/tools/measure, layout only): with the BUILT header's 8 px gaps
 * and 12 px padding the full seat line (86 px en, 89.1 px ar) fitted at no width the app renders (2026-10-01); the
 * mock-up's geometry gives the day line 94 px (en) / 104.5 px (ar) at 390 px - room for the full day (75.7 / 98.3 px)
 * and the seat line - and 64 / 74.5 px at 360 px, room for the short forms only. The consultant set the bound at
 * 390 (2026-10-02); the numbers at 360, 375 and 390 px are in the UI-C41 amendment. Real iOS text metrics differ. */
export const RECORD_DAY_SHORT_BELOW_PX = 390;
/** The media query main.tsx builds for it: strictly narrower than the bound (the .02 keeps 390 itself out). */
export const RECORD_DAY_SHORT_QUERY = `(max-width: ${RECORD_DAY_SHORT_BELOW_PX - 0.02}px)`;
// MAJLIS-66 (mock-up 4b): the "Record deleted" toast on the list shows this long. ASSUMPTION (the mock-up's).
export const RECORD_DELETED_TOAST_MS = 4_000;

// MAJLIS-126 (D-55, UI-C35; the mock-up's ASSUMPTIONS, accepted with the twelve rulings of 2026-09-26): Your rooms keeps
// at most this many rooms on a phone (the oldest falls off) - also the most POST /api/rooms/mine takes (P-35, a test
// pins the two together); an ended room stays, muted, this long after the phone first learns it ended, then drops; each
// landing section shows at most this many rows before "Show all (n)"; and the landing asks the server again this often
// while it is shown (never while in a room).
export const YOUR_ROOMS_MAX = 10;
export const ENDED_ROOM_KEEP_MS = 24 * 60 * 60 * 1000;
export const LANDING_SECTION_ROWS = 3;
export const LANDING_REFRESH_MS = 15_000;
/** MAJLIS-126 (P-36): a room's name is at most this many characters - the server's LISTING_NAME_MAX_LEN (ASSUMPTION
 * there); a test pins the two together. */
export const ROOM_NAME_MAX = 40;

/** MAJLIS-137 (P-38, D-57): what a passive room's join says it speaks. The server does not read it - a passive seat is
 * opened as Arabic, the Arabic-primary-then-router path (hub.PASSIVE_SPEAK_LANG), whatever the join says - but P-1
 * requires the field, so the phone sends the same value rather than anything a person chose. */
export const PASSIVE_SPEAK_LANG = "ar";

/** MAJLIS-164 (the consultant's Addendum 3, option (i), 2026-10-04): the passive room's Auto-play pill shows its SHORT label
 * wherever the FULL one would not fit on one line - per interface language, at a threshold equal to the full label's
 * measured one-line width rounded UP, exactly as MAJLIS-137 set en (165.3 -> 166) and ar (147.3 -> 148). These are those
 * widths; app.css holds one `@container (max-width: <ceil>px)` rule per language, and autoplayThresholds.test.ts holds
 * the two equal (control: a planted rule off by one px is caught).
 * MEASURED (Toronto dev box, headless Chromium 152, web/tools/measure --langs, layout only, 2026-10-04T10:07Z, the tree
 * of b248137 with all 28 catalogues wired; a clone of the full pill at max-content in the passive room's bar; the same
 * px at 320, 360 and 390): ~/majlis-evidence/MAJLIS-164/measure-all-20261004T100654Z/measurements-langs.json. Real iOS
 * text metrics differ (web/tools/measure/README.md). */
export const AUTOPLAY_FULL_LABEL_PX = {
  ar: 147.3,
  en: 165.3,
  "zh-Hans": 84.2,
  "zh-Hant": 84.2,
  de: 182.6,
  fr: 156.7,
  es: 130.7,
  pt: 160.8,
  id: 190.4,
  it: 180.7,
  ko: 80.7,
  ru: 178.2,
  th: 177.8,
  vi: 142.7,
  ja: 91.4,
  tr: 177.7,
  hi: 147.8,
  ms: 162.5,
  nl: 155.9,
  sv: 150.5,
  da: 183.1,
  fi: 158.8,
  pl: 254.9,
  cs: 175.1,
  fil: 141.7,
  fa: 142.9,
  el: 184.7,
  hu: 159,
  mk: 171,
  ro: 162.2,
} as const satisfies Record<UiLang, number>;
